import { useState, useMemo, Suspense } from 'react';
import { Lead } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useMarkLeadDone, useDeferLead, useLogWhatsAppAction, useLogCallAction } from '@/hooks/useLeadsOperations';
import { CreateDealModal } from './CreateDealModal';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TempPill, NeutralStagePill, OutcomeIcon, DealChip } from '@/components/leads/LeadVisual';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { calculateDecisionScore } from '@/lib/decisionEngine';
import {
  Phone,
  Mail,
  MessageCircle,
  Check,
  Clock,
  Edit,
  MapPin,
  Loader2,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { format, isPast, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';

interface LeadDetailsDrawerProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
}

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

// Deal chip for header (open deal value only)
function LeadDealChip({ leadId }: { leadId: string }) {
  const { data: deals } = useDeals();
  const openValue =
    deals
      ?.filter((d) => d.lead_id === leadId && d.stage !== "won" && d.stage !== "lost")
      .reduce((sum, d) => sum + (d.value || 0), 0) ?? 0;
  return <DealChip value={openValue || null} currency="EUR" />;
}

// Heavy data component - loads after drawer opens
function LeadDealsSection({ leadId }: { leadId: string }) {
  const { data: deals } = useDeals();
  const leadDeals = deals?.filter((d) => d.lead_id === leadId) || [];
  const openDeals = leadDeals.filter((d) => d.stage !== "won" && d.stage !== "lost");
  const totalValue = openDeals.reduce((sum, d) => sum + (d.value || 0), 0);

  if (totalValue === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <DollarSign className="w-4 h-4 text-success" />
      <div>
        <p className="text-lg font-bold">{formatCurrency(totalValue)}</p>
        <p className="text-xs text-muted-foreground">Open Deals</p>
      </div>
    </div>
  );
}

export function LeadDetailsDrawer({ lead, open, onOpenChange, onEdit }: LeadDetailsDrawerProps) {
  const [createDealOpen, setCreateDealOpen] = useState(false);
  const markDone = useMarkLeadDone();
  const deferLead = useDeferLead();
  const logWhatsApp = useLogWhatsAppAction();
  const logCall = useLogCallAction();

  // Calculate Decision Score™ immediately (no heavy data dependency)
  const decisionContext = useMemo(() => {
    if (!lead) return null;

    const { score, urgency, reasons } = calculateDecisionScore(lead, 0);
    
    let urgencyReason = reasons[0] || '';
    if (lead.temperature === 'hot') {
      urgencyReason = 'Hot lead - high buying intent';
    } else if (lead.next_action_at && isPast(new Date(lead.next_action_at))) {
      urgencyReason = 'Follow-up overdue';
    } else if (lead.temperature === 'warm') {
      urgencyReason = 'Warm lead - stay engaged';
    }

    const daysSinceContact = lead.last_interaction_at 
      ? differenceInDays(new Date(), new Date(lead.last_interaction_at))
      : null;

    return {
      score,
      urgency,
      urgencyReason,
      daysSinceContact,
    };
  }, [lead]);

  if (!lead) return null;

  const handleWhatsApp = () => {
    if (!lead.phone) return;
    const cleanPhone = lead.phone.replace(/\D/g, '');
    const message = encodeURIComponent(`Hi ${lead.name}, following up on our conversation.`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
    logWhatsApp.mutate(lead.id);
  };

  const handleCall = () => {
    if (!lead.phone) return;
    window.open(`tel:${lead.phone}`, '_self');
    logCall.mutate(lead.id);
  };

  const handleEmail = () => {
    if (!lead.email) return;
    window.open(`mailto:${lead.email}`, '_self');
  };

  const handleMarkDone = async () => {
    await markDone.mutateAsync({ leadId: lead.id });
  };

  const handleDefer = async (days: number) => {
    await deferLead.mutateAsync({ leadId: lead.id, days });
  };

  const isOverdue = lead.next_action_at && isPast(new Date(lead.next_action_at));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-xl">{lead.name}</SheetTitle>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <OutcomeIcon
                  outcome={
                    lead.pipeline_stage === "won"
                      ? "won"
                      : lead.pipeline_stage === "lost"
                        ? "lost"
                        : null
                  }
                />
                <TempPill value={lead.temperature as any} />
                {lead.pipeline_stage &&
                  lead.pipeline_stage !== "won" &&
                  lead.pipeline_stage !== "lost" && (
                    <NeutralStagePill stage={lead.pipeline_stage as any} />
                  )}
                <LeadDealChip leadId={lead.id} />
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onEdit}>
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Decision Score™ Context */}
        {decisionContext && (lead.temperature === 'hot' || lead.temperature === 'warm' || decisionContext.urgency !== 'low') && (
          <>
            <div className={cn(
              'p-4 rounded-lg border mb-4',
              decisionContext.urgency === 'high' && 'bg-destructive/5 border-destructive/20',
              decisionContext.urgency === 'medium' && 'bg-warning/5 border-warning/20',
              decisionContext.urgency === 'low' && 'bg-secondary border-border'
            )}>
              <div className="flex items-center gap-2 mb-3">
                {decisionContext.urgency === 'high' ? (
                  <AlertTriangle className="w-4 h-4 text-destructive" />
                ) : decisionContext.urgency === 'medium' ? (
                  <Zap className="w-4 h-4 text-warning" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={cn(
                  'text-sm font-medium',
                  decisionContext.urgency === 'high' && 'text-destructive',
                  decisionContext.urgency === 'medium' && 'text-warning'
                )}>
                  {decisionContext.urgencyReason}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Suspense fallback={<Skeleton className="h-12 w-full" />}>
                  <LeadDealsSection leadId={lead.id} />
                </Suspense>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Decision Score™</span>
                    <span className="text-sm font-medium">{decisionContext.score}</span>
                  </div>
                  <Progress value={decisionContext.score} className="h-2" />
                </div>
              </div>
            </div>
          </>
        )}

        <Separator className="my-4" />

        {/* Quick Actions */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Quick Actions</h4>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleWhatsApp}
              disabled={!lead.phone}
            >
              <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
              WhatsApp
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleCall}
              disabled={!lead.phone}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call
            </Button>
            <Button
              variant="outline"
              className="justify-start"
              onClick={handleEmail}
              disabled={!lead.email}
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              variant="default"
              className="justify-start"
              onClick={handleMarkDone}
              disabled={markDone.isPending}
            >
              {markDone.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Mark Done
            </Button>
            <Button
              variant="contrast"
              className="justify-start col-span-2"
              onClick={() => setCreateDealOpen(true)}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Create Deal
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => handleDefer(1)}
              disabled={deferLead.isPending}
            >
              <Clock className="w-3 h-3 mr-1" />
              +1 day
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => handleDefer(3)}
              disabled={deferLead.isPending}
            >
              +3 days
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => handleDefer(7)}
              disabled={deferLead.isPending}
            >
              +1 week
            </Button>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Contact Info */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Contact Info</h4>
          <div className="space-y-2">
            {lead.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{lead.email}</span>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{lead.phone}</span>
              </div>
            )}
            {lead.geographic_country && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>
                  {[lead.geographic_city, lead.geographic_region, lead.geographic_country]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>

        <Separator className="my-4" />

        {/* Details */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Source</span>
              <span className="capitalize">{lead.source || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Priority</span>
              <Badge variant={
                lead.decision_priority === 'high' ? 'destructive' : 
                lead.decision_priority === 'medium' ? 'secondary' : 'outline'
              } className="text-xs">
                {lead.decision_priority || 'Medium'}
              </Badge>
            </div>
            {lead.next_action_at && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Next Follow-up</span>
                <span className={cn(
                  isOverdue && 'text-destructive font-medium'
                )}>
                  {isOverdue && '⚠ '}
                  {format(new Date(lead.next_action_at), 'MMM d, h:mm a')}
                </span>
              </div>
            )}
            {lead.last_interaction_at && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Contact</span>
                <span>{format(new Date(lead.last_interaction_at), 'MMM d, yyyy')}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Created</span>
              <span>{format(new Date(lead.created_at), 'MMM d, yyyy')}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {(lead.notes_short || lead.notes_long) && (
          <>
            <Separator className="my-4" />
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Notes</h4>
              {lead.notes_short && (
                <p className="text-sm">{lead.notes_short}</p>
              )}
              {lead.notes_long && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {lead.notes_long}
                </p>
              )}
            </div>
          </>
        )}
      </SheetContent>

      {/* Create Deal Modal */}
      <CreateDealModal 
        lead={lead} 
        open={createDealOpen} 
        onOpenChange={setCreateDealOpen} 
      />
    </Sheet>
  );
}
